import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Info, Megaphone, Wand2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  SURFACES,
  SlotDef,
  describeDefaultSource,
  pillarName,
} from '@/client/types/highlights/slotRegistry';
import { HighlightOverridePanel } from './HighlightOverridePanel';
import { useSlotCardQuery } from '@/client/api/highlights/highlights.query';
import { HighlightPinsPanel } from './HighlightPinsPanel';

interface Props {
  slot: SlotDef | null;
  surfaceName: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

/** Every place the same category card appears, so an editor knows the blast radius. */
const otherSpotsSharing = (slot: SlotDef): string[] => {
  if (slot.etid == null) return [];
  const out: string[] = [];
  for (const surface of SURFACES) {
    for (const section of surface.sections) {
      for (const s of section.slots) {
        if (s.etid === slot.etid && s.card === slot.card && s.key !== slot.key) {
          out.push(`${surface.name} · ${s.name}`);
        }
      }
    }
  }
  return out;
};

function InfoCallout({
  icon,
  tone,
  children,
}: {
  icon: React.ReactNode;
  tone: 'orange' | 'slate' | 'blue';
  children: React.ReactNode;
}) {
  const cls = {
    orange: 'border-orange-100 bg-orange-50 text-orange-900',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
    blue: 'border-blue-100 bg-blue-50 text-blue-900',
  }[tone];
  return (
    <div className={`flex gap-2.5 rounded-xl border p-3.5 text-sm ${cls}`}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

export function SlotDetailSheet({ slot, surfaceName, open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const slotCard = useSlotCardQuery(slot && slot.feed === 'highlights' && !slot.dynamic ? slot : null);
  if (!slot) return null;

  const editable = slot.feed === 'highlights' && slot.etid != null && !slot.dynamic;
  const shared = editable ? otherSpotsSharing(slot) : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-lg">
        <SheetHeader className="space-y-1 border-b border-gray-100 bg-white p-5">
          <p className="text-xs font-medium uppercase tracking-wide text-blue-600">{surfaceName}</p>
          <SheetTitle className="text-lg font-bold text-gray-900">{slot.name}</SheetTitle>
          <p className="text-sm text-gray-500">
            {slot.position}
            {slot.slides > 1 ? ` · rotates up to ${slot.slides} cards` : ' · one banner'}
          </p>
        </SheetHeader>

        <div className="space-y-4 p-5">
          {/* What shows here now — always explained in plain language. */}
          <InfoCallout icon={<Info className="h-4 w-4" />} tone="blue">
            <p className="font-medium">What shows here now</p>
            <p className="text-blue-800">{describeDefaultSource(slot)}</p>
          </InfoCallout>

          {slot.feed === 'promotions' && (
            <InfoCallout icon={<Megaphone className="h-4 w-4" />} tone="orange">
              <p className="font-medium">This is a paid banner spot.</p>
              <p className="text-orange-800">
                Paid campaigns fill hero slots first. Slider Content is where you set what shows when they do not.
              </p>
              <Button size="sm" className="mt-1.5 gap-1.5" onClick={() => navigate('/admin/promotions/slider-content')}>
                Open Slider Content
                <ArrowRight className="h-4 w-4" />
              </Button>
            </InfoCallout>
          )}

          {slot.dynamic && (
            <InfoCallout icon={<Wand2 className="h-4 w-4" />} tone="slate">
              <p className="font-medium">This tile follows the shopper.</p>
              <p>
                Menu tiles automatically show cards from whichever category the shopper is browsing, so there's nothing
                to set per-tile. To change what a category shows, edit that category's card from its page above.
              </p>
            </InfoCallout>
          )}

          {editable && (
            <>
              {shared.length > 0 && (
                <InfoCallout icon={<Info className="h-4 w-4" />} tone="slate">
                  <p className="font-medium">Heads up — this {pillarName(slot.etid)} card is shared.</p>
                  <p>
                    The same card also appears in: {shared.join(', ')}. Changes here affect all of them.
                  </p>
                </InfoCallout>
              )}
              {slotCard.data ? (
                <>
                  {slotCard.data.label && (
                    <p className="text-sm text-gray-600">
                      This slot currently shows the <span className="font-medium text-gray-900">{slotCard.data.label}</span>{' '}
                      card. Edits below apply to that card.
                    </p>
                  )}
                  <HighlightOverridePanel etid={slotCard.data.etid} />
                  <HighlightPinsPanel etid={slotCard.data.etid} />
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  {slotCard.isLoading
                    ? 'Finding the card this slot shows…'
                    : 'No card is showing in this slot right now, so there is nothing to edit.'}
                </p>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
